import { c as _c } from "react/compiler-runtime";
import { IcoCloseSmall16, IcoGlobe16, IcoMaximizeSmall16 } from '@onefootprint/icons';
import type { BrowserSessionResource } from '@onefootprint/request-types/percy';
import { cx } from 'class-variance-authority';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { hostOf } from '../../utils/host-of';
import { BrowserStatusBadge, browserStatus } from './browser-status';
type BrowserPreviewCardProps = {
  session: BrowserSessionResource;
  /** Percy is still on its turn; off once it has answered and the browser sits idle. */
  working: boolean;
  onExpand: () => void;
  onDismiss: () => void;
  /** The card's face. The live view layer sits under the card and shows through it. */
  frameSlotRef: RefObject<HTMLDivElement | null>;
};

// The chrome floats over a page whose colors we don't control, so it stays dark glass in both
// themes rather than following the surface tokens.
const OVERLAY = 'bg-black/60 backdrop-blur-sm text-white border border-solid border-white/15';
const badgeClassName = cx('flex items-center gap-1.5 h-6 px-2 rounded-md', OVERLAY);
const circleClassName = cx(
// biome-ignore lint/plugin: a scrim over the page screenshot, so it stays dark in both themes.
'flex items-center justify-center rounded-full shadow-md transition-colors duration-100 hover:bg-black/75', OVERLAY);
// A touch screen has no hover to reveal the controls, so a phone shows them at rest.
const revealed = 'opacity-0 max-md:opacity-100 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none';
const focusRing = 'focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';
const BrowserPreviewCard = t0 => {
  const $ = _c(39);
  const {
    session,
    working,
    onExpand,
    onDismiss,
    frameSlotRef
  } = t0;
  const {
    t
  } = useTranslation("copilot");
  let t1;
  if ($[0] !== session.current_url) {
    t1 = hostOf(session.current_url);
    $[0] = session.current_url;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const host = t1;
  let t2;
  if ($[2] !== frameSlotRef) {
    t2 = <div ref={frameSlotRef} className="absolute inset-0 rounded-lg" />;
    $[2] = frameSlotRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t(_temp);
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cx("absolute inset-0 rounded-lg flex items-center justify-center cursor-pointer", focusRing);
    t5 = <span className={cx("size-8", circleClassName, revealed)}><IcoMaximizeSmall16 /></span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onExpand || $[9] !== t3) {
    t6 = <button type="button" aria-label={t3} onClick={onExpand} className={t4}>{t5}</button>;
    $[8] = onExpand;
    $[9] = t3;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t(_temp2);
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cx("absolute top-2 left-2 z-20 size-7 cursor-pointer", circleClassName, revealed, focusRing);
    t9 = <IcoCloseSmall16 />;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onDismiss || $[16] !== t7) {
    t10 = <button type="button" aria-label={t7} onClick={onDismiss} className={t8}>{t9}</button>;
    $[15] = onDismiss;
    $[16] = t7;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cx("absolute bottom-2 left-2 z-10 max-w-[112px] md:max-w-[150px]", badgeClassName);
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IcoGlobe16 className="shrink-0" />;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== host || $[21] !== t) {
    t13 = host || t(_temp3);
    $[20] = host;
    $[21] = t;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13) {
    t14 = <span className="text-caption-4 truncate">{t13}</span>;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== session.current_url || $[26] !== t14) {
    t15 = <span className={t11} title={session.current_url}>{t12}{t14}</span>;
    $[25] = session.current_url;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== working) {
    t16 = browserStatus(false, working);
    $[28] = working;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cx("absolute bottom-2 right-2 z-10", badgeClassName);
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16) {
    t18 = <BrowserStatusBadge status={t16} tone="overlay" className={t17} />;
    $[31] = t16;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t10 || $[34] !== t15 || $[35] !== t18 || $[36] !== t2 || $[37] !== t6) {
    t19 = <div className="group relative w-[216px] h-[120px] md:w-[288px] md:h-[160px] rounded-lg border border-solid border-tertiary shadow-lg">{t2}{t6}{t10}{t15}{t18}</div>;
    $[33] = t10;
    $[34] = t15;
    $[35] = t18;
    $[36] = t2;
    $[37] = t6;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  return t19;
};
export default BrowserPreviewCard;
function _temp($) {
  return $.chat.browser.expand;
}
function _temp2($_0) {
  return $_0.chat.browser.close;
}
function _temp3($_1) {
  return $_1.chat.browser.starting;
}